// Azure Static Web Apps Easy Auth
// This service uses the built-in authentication provided by Azure Static Web Apps

export interface UserInfo {
  userId: string;
  userDetails: string;
  userRoles: string[];
  identityProvider: string;
}

// Check if running in local development mode
export const IS_LOCAL_MODE = typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

// Bypass token session storage key
const BYPASS_SESSION_KEY = 'bypass_session';

export interface BypassSession {
  userId: string;
  email: string;
  displayName: string;
  apiKey: string;
}

export function getBypassSession(): BypassSession | null {
  try {
    const raw = localStorage.getItem(BYPASS_SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function setBypassSession(session: BypassSession): void {
  localStorage.setItem(BYPASS_SESSION_KEY, JSON.stringify(session));
}

export function clearBypassSession(): void {
  localStorage.removeItem(BYPASS_SESSION_KEY);
}

// Get current user information from Azure Static Web Apps Easy Auth
export async function getCurrentUser(): Promise<UserInfo | null> {
  if (IS_LOCAL_MODE) {
    // In local mode, return mock user
    return {
      userId: 'local-test-user',
      userDetails: 'Local Developer',
      userRoles: ['authenticated', 'admin'],
      identityProvider: 'local',
    };
  }

  // Check bypass session first
  const bypassSession = getBypassSession();
  if (bypassSession) {
    return {
      userId: bypassSession.userId,
      userDetails: bypassSession.email,
      userRoles: ['authenticated'],
      identityProvider: 'bypass',
    };
  }

  try {
    const response = await fetch('/.auth/me');
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    const clientPrincipal = data.clientPrincipal;

    if (!clientPrincipal) {
      return null;
    }

    return {
      userId: clientPrincipal.userId,
      userDetails: clientPrincipal.userDetails,
      userRoles: clientPrincipal.userRoles || [],
      identityProvider: clientPrincipal.identityProvider,
    };
  } catch (error) {
    console.error('Failed to fetch user info:', error);
    return null;
  }
}

// Login with API Key (for users blocked by Entra ID admin consent)
export async function loginWithApiKey(email: string, apiKey: string): Promise<BypassSession> {
  const response = await fetch('/api/auth/token-login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, apiKey }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Login failed' }));
    throw new Error(error.error || 'Login failed');
  }

  const session: BypassSession = await response.json();
  setBypassSession(session);
  return session;
}

// Login using Azure Static Web Apps Easy Auth
export function login() {
  if (IS_LOCAL_MODE) {
    // In local mode, just reload
    window.location.href = '/';
    return;
  }
  // Redirect to Azure AD login
  window.location.href = '/.auth/login/aad';
}

// Logout using Azure Static Web Apps Easy Auth
export function logout() {
  if (IS_LOCAL_MODE) {
    // In local mode, just reload
    window.location.href = '/';
    return;
  }
  // Clear bypass session if exists
  clearBypassSession();
  // Redirect to Azure AD logout
  window.location.href = '/.auth/logout';
}

// Legacy exports for backward compatibility
export const msalInstance = null;
export async function initializeMsal() {
  // No-op for Easy Auth
}
export const loginRequest = { scopes: [] };
